// src/components/groups/Groups.tsx

import { AxiosError } from 'axios';
import React, {useMemo, useRef, useState} from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Group as GroupIcon,
    People as PeopleIcon,
    Person as PersonIcon,
    Search as SearchIcon,
    Sort as SortIcon,
} from '@mui/icons-material';
import {useAppContext} from '../../context/AppContext';
import ModalDialog from '../common/ModalDialog';
import GroupForm, {GroupFormRef} from './GroupForm';
import {CreateGroupDto, Group} from '../../types';
import format from 'date-fns/format';
import ru from 'date-fns/locale/ru';

type SortField = 'name' | 'studentsCount' | 'nextLessonDate' | 'totalPrice';
type SortOrder = 'asc' | 'desc';

const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.data) {
            const data = axiosError.response.data as any;

            if (data.error && typeof data.error === 'string') {
                return data.error;
            }

            if (data.message && typeof data.message === 'string') {
                return data.message;
            }

            if (data.errors && Array.isArray(data.errors)) {
                return data.errors.join(', ');
            }

            if (typeof data === 'string') {
                return data;
            }
        }

        if (axiosError.response?.status) {
            const status = axiosError.response.status;
            switch (status) {
                case 400:
                    return 'Неверный запрос. Проверьте введенные данные.';
                case 401:
                    return 'Необходима авторизация.';
                case 403:
                    return 'Доступ запрещен.';
                case 404:
                    return 'Запрашиваемый ресурс не найден.';
                case 409:
                    return 'Конфликт данных. Возможно, такая группа уже существует.';
                case 500:
                    return 'Внутренняя ошибка сервера. Попробуйте позже.';
                default:
                    return `Ошибка сервера (${status}). Попробуйте позже.`;
            }
        }

        if (axiosError.message) {
            return axiosError.message;
        }
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return 'Произошла неизвестная ошибка. Попробуйте позже.';
};

const Groups: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { groups, students, lessons, addGroup, updateGroup, deleteGroup, loading } = useAppContext();

    const formRef = useRef<GroupFormRef>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<Group | null>(null);
    const [formLoading, setFormLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Состояния для сортировки
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);
    const [deleteWithLessons, setDeleteWithLessons] = useState(false);

    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // Функция для естественной сортировки строк с числами
    const naturalCompare = (a: string, b: string): number => {
        // Разбиваем строки на части (числа и текст)
        const aParts = a.match(/(\d+)|(\D+)/g) || [];
        const bParts = b.match(/(\d+)|(\D+)/g) || [];

        let i = 0;
        while (i < aParts.length && i < bParts.length) {
            const aPart = aParts[i];
            const bPart = bParts[i];

            // Если обе части - числа, сравниваем как числа
            if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
                const aNum = parseInt(aPart, 10);
                const bNum = parseInt(bPart, 10);
                if (aNum !== bNum) {
                    return aNum - bNum;
                }
            } else {
                // Иначе сравниваем как строки
                const result = aPart.localeCompare(bPart);
                if (result !== 0) {
                    return result;
                }
            }
            i++;
        }

        // Если одна строка закончилась, она короче
        return aParts.length - bParts.length;
    };

    // Получить учеников группы с проверкой на null
    const getGroupStudents = (group: Group) => {
        if (!group.studentIds || !Array.isArray(group.studentIds)) {
            return [];
        }
        return students.filter(s => group.studentIds.includes(s.id) && s.isActive);
    };

    // Получить общую стоимость уроков группы
    const getGroupTotalPrice = (group: Group) => {
        const groupStudents = getGroupStudents(group);
        return groupStudents.reduce((sum, student) => sum + student.pricePerLesson, 0);
    };

    // Получить количество уроков группы
    const getGroupLessonsCount = (group: Group) => {
        return lessons.filter(l => l.groupId === group.id).length;
    };

    // Получить дату ближайшего занятия
    const getNextLessonDate = (group: Group) => {
        const now = new Date();
        const groupLessons = lessons
            .filter(l => l.groupId === group.id)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const nextLesson = groupLessons.find(l => {
            const lessonDate = new Date(l.date);
            return lessonDate > now;
        });
        return nextLesson ? new Date(nextLesson.date) : null;
    };

    // Функция форматирования цены
    const formatPrice = (price: number): string => {
        const rounded = Math.round(price * 100) / 100;
        return rounded.toLocaleString('ru-RU', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    // Форматирование даты
    const formatDate = (date: Date | null) => {
        if (!date) return 'Нет занятий';
        try {
            return format(date, 'd MMMM, HH:mm', { locale: ru });
        } catch {
            return 'Нет занятий';
        }
    };

    // Создаем расширенный объект группы с вычисляемыми полями
    const groupsWithStats = useMemo(() => {
        return groups.map(group => {
            const groupStudents = getGroupStudents(group);
            const totalPrice = getGroupTotalPrice(group);
            const nextLessonDate = getNextLessonDate(group);

            return {
                ...group,
                studentsCount: groupStudents.length,
                totalPrice,
                nextLessonDate,
                groupStudents,
            };
        });
    }, [groups, students, lessons]);

    // Фильтрация и сортировка групп
    const filteredAndSortedGroups = useMemo(() => {
        // Сначала фильтруем
        let result = groupsWithStats.filter(group =>
            group.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Затем сортируем
        result.sort((a, b) => {
            let comparison = 0;

            switch (sortField) {
                case 'name':
                    // Используем естественную сортировку для названий
                    comparison = naturalCompare(a.name, b.name);
                    break;
                case 'studentsCount':
                    comparison = a.studentsCount - b.studentsCount;
                    break;
                case 'totalPrice':
                    comparison = a.totalPrice - b.totalPrice;
                    break;
                case 'nextLessonDate':
                    if (!a.nextLessonDate && !b.nextLessonDate) {
                        comparison = 0;
                    } else if (!a.nextLessonDate) {
                        comparison = 1;
                    } else if (!b.nextLessonDate) {
                        comparison = -1;
                    } else {
                        comparison = a.nextLessonDate.getTime() - b.nextLessonDate.getTime();
                    }
                    break;
                default:
                    comparison = 0;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [groupsWithStats, searchTerm, sortField, sortOrder]);

    // Обработчик изменения поля сортировки
    const handleSortFieldChange = (event: SelectChangeEvent) => {
        setSortField(event.target.value as SortField);
    };

    // Обработчик изменения порядка сортировки
    const handleSortOrderToggle = () => {
        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const handleOpenCreate = () => {
        setEditingGroup(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (group: Group) => {
        setEditingGroup(group);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingGroup(null);
        setFormLoading(false);
    };

    const handleSaveGroup = async (data: CreateGroupDto) => {
        setFormLoading(true);
        try {
            if (editingGroup) {
                await updateGroup(editingGroup.id, data);
                showNotification('Группа успешно обновлена', 'success');
            } else {
                await addGroup(data);
                showNotification('Группа успешно создана', 'success');
            }
            handleCloseModal();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
            setFormLoading(false);
        }
    };

    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    const handleOpenDelete = (group: Group) => {
        setDeletingGroup(group);
        setDeleteWithLessons(false);
        setDeleteDialogOpen(true);
    };

    const handleDeleteGroup = async () => {
        if (!deletingGroup) return;

        try {
            await deleteGroup(deletingGroup.id, deleteWithLessons);
            showNotification(
                `Группа "${deletingGroup.name}" удалена${deleteWithLessons ? ' с уроками' : ''}`,
                'success'
            );
            setDeleteDialogOpen(false);
            setDeletingGroup(null);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setNotification({ open: true, message, severity });
    };

    // Получить текст для отображения направления сортировки
    const getSortOrderLabel = (order: SortOrder) => {
        return order === 'asc' ? '↑' : '↓';
    };

    return (
        <Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
                mb: 3
            }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 500 }}>
                    👥 Группы
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    disabled={loading}
                    fullWidth={isMobile}
                >
                    Создать группу
                </Button>
            </Box>

            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2
                }}>
                    {/* Поиск */}
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Поиск групп..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {/* Сортировка */}
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flex: { xs: 1, md: 'none' },
                        minWidth: { md: 300 },
                    }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Сортировать по</InputLabel>
                            <Select
                                value={sortField}
                                onChange={handleSortFieldChange}
                                label="Сортировать по"
                            >
                                <MenuItem value="name">Названию</MenuItem>
                                <MenuItem value="studentsCount">Учеников</MenuItem>
                                <MenuItem value="totalPrice">Цене</MenuItem>
                                <MenuItem value="nextLessonDate">Дате</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSortOrderToggle}
                            sx={{ minWidth: '48px' }}
                        >
                            {getSortOrderLabel(sortOrder)}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Карточки групп */}
            {filteredAndSortedGroups.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {groups.length === 0
                            ? 'Нет групп. Создайте первую группу!'
                            : 'Группы не найдены по заданному поиску'}
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {filteredAndSortedGroups.map((group) => {
                        const { groupStudents, totalPrice, nextLessonDate } = group;
                        const lessonsCount = getGroupLessonsCount(group);

                        return (
                            <Grid key={group.id} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8],
                                        },
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        {/* Заголовок карточки */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.light',
                                                    mr: 2,
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                <GroupIcon />
                                            </Avatar>
                                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, flex: 1 }}>
                                                {group.name}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ mb: 2 }} />

                                        {/* Ученики */}
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <PeopleIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Ученики:
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {groupStudents.length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Нет учеников
                                                    </Typography>
                                                ) : (
                                                    groupStudents.map((student) => (
                                                        <Chip
                                                            key={student.id}
                                                            label={student.fullName}
                                                            size="small"
                                                            variant="outlined"
                                                            icon={<PersonIcon />}
                                                        />
                                                    ))
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Статистика */}
                                        <Grid container spacing={1.5} sx={{ mt: 1 }}>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{
                                                    p: 1,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1,
                                                    textAlign: 'center',
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Учеников
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {groupStudents.length}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{
                                                    p: 1,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1,
                                                    textAlign: 'center',
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Общая цена
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                        {formatPrice(totalPrice)} ₽
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{
                                                    p: 1,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1,
                                                    textAlign: 'center',
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Всего занятий
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {lessonsCount}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{
                                                    p: 1,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 1,
                                                    textAlign: 'center',
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Ближайшее
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                        {formatDate(nextLessonDate)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>

                                    {/* Кнопки действий */}
                                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<EditIcon />}
                                            onClick={() => handleOpenEdit(group)}
                                            disabled={loading}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Редактировать
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleOpenDelete(group)}
                                            disabled={loading}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Удалить
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Модальное окно создания/редактирования */}
            <ModalDialog
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveClick}
                title={editingGroup ? 'Редактирование группы' : 'Создание группы'}
                loading={formLoading}
                disabled={formLoading}
            >
                <GroupForm
                    ref={formRef}
                    initialData={editingGroup}
                    students={students}
                    onSubmit={handleSaveGroup}
                    loading={formLoading}
                />
            </ModalDialog>

            {/* Диалог удаления группы */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Удаление группы</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Вы уверены, что хотите удалить группу "{deletingGroup?.name}"?
                        {deletingGroup && students.filter(s =>
                            deletingGroup.studentIds &&
                            Array.isArray(deletingGroup.studentIds) &&
                            deletingGroup.studentIds.includes(s.id)
                        ).length > 0 && (
                            <>
                                <br /><br />
                                В группе {students.filter(s =>
                                deletingGroup.studentIds &&
                                Array.isArray(deletingGroup.studentIds) &&
                                deletingGroup.studentIds.includes(s.id)
                            ).length} учеников.
                                При удалении группы ученики будут откреплены от нее.
                            </>
                        )}
                    </DialogContentText>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deleteWithLessons}
                                onChange={(e) => setDeleteWithLessons(e.target.checked)}
                            />
                        }
                        label="Удалить прошедшие уроки группы"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
                    <Button onClick={handleDeleteGroup} color="error" variant="contained">
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Уведомления */}
            <Snackbar
                open={notification.open}
                autoHideDuration={5000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Groups;